import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { MobileNav } from '../components/MobileNav';
import {
  Shield, Plus, Edit, Trash2, Package, ShoppingBag,
  X, CreditCard, Sparkles, Settings, Truck, Check, Ban,
  Users as UsersIcon, History, Coins, Lock, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';
import { ImageUpload } from '../components/ImageUpload';
import { ImageGalleryUpload } from '../components/ImageGalleryUpload';
import { KujiAdminPanel } from './KujiAdminPanel';
import { HeroBannersManager } from '../components/HeroBannersManager';
import { PromoBannersManager } from '../components/PromoBannersManager';
import { ShippingOriginManager } from '../components/ShippingOriginManager';
import { ProductVariantManager } from '../components/ProductVariantManager';
import { AdminOrderManager } from '../components/AdminOrderManager';
import { AdminBalanceModal } from '../components/AdminBalanceModal';
import { CategoriesManager } from '../components/CategoriesManager';
import { UsersTabWithAnalytics } from '../components/UsersTabWithAnalytics';
import { TiersManager } from '../components/TiersManager';

const ORDER_STATUS_LABEL = {
  awaiting_shipping_quote: 'Menunggu Quote Ongkir',
  pending_payment:  'Menunggu Pembayaran',
  payment_submitted:'Bukti Terkirim',
  payment_verified: 'Pembayaran Diverifikasi',
  processing:       'Diproses',
  packed:           'Dikemas',
  shipped:          'Dikirim',
  delivered:        'Diterima',
  completed:        'Selesai',
  cancelled:        'Dibatalkan',
};
const ORDER_STATUS_TONE = {
  awaiting_shipping_quote: { bg: 'rgba(245,158,11,0.18)', fg: '#F59E0B' },
  pending_payment:  { bg: 'rgba(245,158,11,0.15)', fg: '#F59E0B' },
  payment_submitted:{ bg: 'rgba(168,85,247,0.18)', fg: '#A855F7' },
  payment_verified: { bg: 'rgba(59,130,246,0.15)', fg: '#3B82F6' },
  processing:       { bg: 'rgba(59,130,246,0.15)', fg: '#3B82F6' },
  packed:           { bg: 'rgba(168,85,247,0.15)', fg: '#A855F7' },
  shipped:          { bg: 'rgba(212,175,55,0.18)', fg: '#D4AF37' },
  delivered:        { bg: 'rgba(34,197,94,0.15)',  fg: '#22C55E' },
  completed:        { bg: 'rgba(34,197,94,0.2)',   fg: '#22C55E' },
  cancelled:        { bg: 'rgba(239,68,68,0.15)',  fg: '#EF4444' },
};

const playfairFont = "'Playfair Display', serif";

const tabs = [
  { id: 'products', label: 'Products', icon: Package },
  { id: 'categories', label: 'Categories', icon: Sparkles },
  { id: 'tiers', label: 'Tiers', icon: Trophy },
  { id: 'orders', label: 'Orders', icon: ShoppingBag },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'kuji', label: 'Kuji/Gacha', icon: Trophy },
  { id: 'hero', label: 'Hero Banners', icon: Sparkles },
  { id: 'promo', label: 'Promo Banners', icon: Sparkles },
  { id: 'campaigns', label: 'Lucky Draw (legacy)', icon: Sparkles },
  { id: 'shipping', label: 'Shipping', icon: Truck },
  { id: 'shipping-origin', label: 'Shipping Origin', icon: Truck },
  { id: 'users', label: 'Users', icon: UsersIcon },
  { id: 'transactions', label: 'Transactions', icon: Coins },
  { id: 'drawlogs', label: 'Draw Logs', icon: History },
  { id: 'otplogs', label: 'OTP Logs', icon: Shield },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const formatPrice = (price) => new Intl.NumberFormat('id-ID', {
  style: 'currency', currency: 'IDR', minimumFractionDigits: 0
}).format(price);

const formatCoins = (coins) => new Intl.NumberFormat('id-ID').format(coins);

export const Admin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(true);

  // Data
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [balanceModalUser, setBalanceModalUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [shippingRequests, setShippingRequests] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState({ qris_image_url: '', bank_name: 'Mandiri', mandiri_account_number: '', mandiri_account_holder: '' });
  const [coinPackages, setCoinPackages] = useState([]);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [usersList, setUsersList] = useState([]);
  const [transactionsList, setTransactionsList] = useState([]);
  const [drawLogs, setDrawLogs] = useState([]);
  const [otpLogs, setOtpLogs] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [adjustModal, setAdjustModal] = useState(null); // user_id when open
  const [adjustForm, setAdjustForm] = useState({ amount: '', description: '', currency: 'coins' }); // currency: 'coins' or 'tickets'

  // Forms
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', original_price: '', discount_percent: '',
    images: [], category: '', stock: '', weight_grams: '', is_flash_sale: false,
    flash_sale_end_time: '', flash_sale_stock: '', variants: []
  });

  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '', description: '', coin_cost: '', start_date: '', end_date: '', active: true
  });

  const [showPrizeForm, setShowPrizeForm] = useState(null); // campaign_id when open
  const [prizeForm, setPrizeForm] = useState({
    name: '', description: '', image_url: '', rarity: 'common', probability: '', stock: ''
  });
  const [campaignPrizes, setCampaignPrizes] = useState({}); // {campaign_id: [prizes]}

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
    // Refresh categories whenever Admin re-mounts or tab changes (so dropdown stays fresh)
    axios.get(`${API}/admin/categories`, { withCredentials: true }).then((r) => setCategories(r.data || [])).catch(() => {});
  }, [user, navigate, activeTab]);

  const fetchData = async () => {
    // Kuji tab manages its own loading internally; skip parent fetch.
    if (activeTab === 'kuji' || activeTab === 'hero' || activeTab === 'promo' || activeTab === 'shipping-origin' || activeTab === 'categories' || activeTab === 'tiers') {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (activeTab === 'products') {
        const { data } = await axios.get(`${API}/products`);
        setProducts(data);
      } else if (activeTab === 'orders') {
        const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
        setOrders(data);
      } else if (activeTab === 'payments') {
        const { data } = await axios.get(`${API}/admin/payments/pending`, { withCredentials: true });
        setPayments(data);
      } else if (activeTab === 'campaigns') {
        const { data } = await axios.get(`${API}/admin/lucky-draws/campaigns`, { withCredentials: true });
        setCampaigns(data);
        // Load prizes for each campaign
        const prizesMap = {};
        for (const c of data) {
          try {
            const res = await axios.get(`${API}/admin/lucky-draws/prizes/${c.id}`, { withCredentials: true });
            prizesMap[c.id] = res.data;
          } catch (e) { prizesMap[c.id] = []; }
        }
        setCampaignPrizes(prizesMap);
      } else if (activeTab === 'shipping') {
        const { data } = await axios.get(`${API}/admin/inventory/requests`, { withCredentials: true });
        setShippingRequests(data);
      } else if (activeTab === 'settings') {
        const [settingsRes, packagesRes] = await Promise.all([
          axios.get(`${API}/admin/payment-settings`, { withCredentials: true }),
          axios.get(`${API}/coin-packages`, { withCredentials: true })
        ]);
        setPaymentSettings(settingsRes.data);
        setCoinPackages(packagesRes.data.packages || []);
      } else if (activeTab === 'users') {
        const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
        setUsersList(data);
      } else if (activeTab === 'transactions') {
        const { data } = await axios.get(`${API}/admin/transactions`, { withCredentials: true });
        setTransactionsList(data);
      } else if (activeTab === 'drawlogs') {
        const { data } = await axios.get(`${API}/admin/lucky-draws/history`, { withCredentials: true });
        setDrawLogs(data);
      } else if (activeTab === 'otplogs') {
        const [otpRes, emailRes] = await Promise.all([
          axios.get(`${API}/admin/otp-logs?limit=200`, { withCredentials: true }),
          axios.get(`${API}/admin/email-logs?limit=100`, { withCredentials: true }),
        ]);
        setOtpLogs(otpRes.data.items || []);
        setEmailLogs(emailRes.data.items || []);
      }
    } catch (e) {
      console.error('Failed to fetch:', e);
    } finally {
      setLoading(false);
    }
  };

  // ===== Products =====
  const resetProductForm = () => setProductForm({
    name: '', description: '', price: '', original_price: '', discount_percent: '',
    images: [], category: '', stock: '', weight_grams: '', is_flash_sale: false,
    flash_sale_end_time: '', flash_sale_stock: '', variants: []
  });

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.images || productForm.images.length === 0) {
      toast.error('Mohon upload minimal 1 gambar produk');
      return;
    }
    if (!productForm.weight_grams || parseInt(productForm.weight_grams) <= 0) {
      toast.error('Berat produk wajib diisi.');
      return;
    }
    const payload = {
      ...productForm,
      price: parseFloat(productForm.price),
      original_price: productForm.original_price ? parseFloat(productForm.original_price) : parseFloat(productForm.price),
      discount_percent: productForm.discount_percent ? parseInt(productForm.discount_percent) : 0,
      stock: parseInt(productForm.stock),
      weight_grams: parseInt(productForm.weight_grams),
      images: productForm.images,
      image_url: productForm.images[0],
      flash_sale_stock: productForm.flash_sale_stock ? parseInt(productForm.flash_sale_stock) : null,
      flash_sale_end_time: productForm.flash_sale_end_time || null,
      variants: (productForm.variants || []).map((g) => ({
        name: g.name,
        required: g.required !== false,
        options: (g.options || []).map((o) => ({
          value: String(o.value),
          stock: parseInt(o.stock) || 0,
          price_adjust: parseInt(o.price_adjust) || 0,
          price_override: (o.price_override === '' || o.price_override === null || o.price_override === undefined) ? null : parseInt(o.price_override),
          sku: o.sku || null,
        })),
      })),
    };
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, { withCredentials: true });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, payload, { withCredentials: true });
        toast.success('Product added');
      }
      setShowProductForm(false);
      setEditingProduct(null);
      resetProductForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEditProduct = (p) => {
    setEditingProduct(p);
    setProductForm({
      name: p.name, description: p.description, price: p.price,
      original_price: p.original_price || p.price, discount_percent: p.discount_percent || 0,
      images: p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : []),
      category: p.category, stock: p.stock, weight_grams: p.weight_grams || '',
      is_flash_sale: p.is_flash_sale,
      flash_sale_end_time: p.flash_sale_end_time ? p.flash_sale_end_time.slice(0, 16) : '',
      flash_sale_stock: p.flash_sale_stock || '',
      variants: p.variants || []
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  // ===== Payments =====
  const handleVerifyPayment = async (paymentId, action) => {
    try {
      await axios.post(`${API}/admin/payments/verify`,
        { payment_id: paymentId, action, admin_notes: '' },
        { withCredentials: true }
      );
      toast.success(`Payment ${action}d`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  // ===== Campaigns =====
  const handleCampaignSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/lucky-draws/campaigns`, {
        name: campaignForm.name,
        description: campaignForm.description,
        coin_cost: parseInt(campaignForm.coin_cost),
        start_date: new Date(campaignForm.start_date).toISOString(),
        end_date: new Date(campaignForm.end_date).toISOString(),
        active: campaignForm.active
      }, { withCredentials: true });
      toast.success('Campaign created');
      setShowCampaignForm(false);
      setCampaignForm({ name: '', description: '', coin_cost: '', start_date: '', end_date: '', active: true });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleToggleCampaign = async (c) => {
    try {
      await axios.put(`${API}/admin/lucky-draws/campaigns/${c.id}`, {
        ...c,
        active: !c.active,
        start_date: c.start_date,
        end_date: c.end_date
      }, { withCredentials: true });
      toast.success(`Campaign ${!c.active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const handlePrizeSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/lucky-draws/prizes`, {
        campaign_id: showPrizeForm,
        name: prizeForm.name,
        description: prizeForm.description,
        image_url: prizeForm.image_url,
        rarity: prizeForm.rarity,
        probability: parseFloat(prizeForm.probability),
        stock: parseInt(prizeForm.stock)
      }, { withCredentials: true });
      toast.success('Prize added');
      setShowPrizeForm(null);
      setPrizeForm({ name: '', description: '', image_url: '', rarity: 'common', probability: '', stock: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDeletePrize = async (prizeId) => {
    if (!window.confirm('Delete this prize?')) return;
    try {
      await axios.delete(`${API}/admin/lucky-draws/prizes/${prizeId}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  // ===== Shipping =====
  const handleMarkShipped = async (itemId) => {
    try {
      await axios.post(`${API}/admin/inventory/${itemId}/ship`, {}, { withCredentials: true });
      toast.success('Marked as shipped');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  // ===== Settings =====
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/payment-settings`, paymentSettings, { withCredentials: true });
      toast.success('Settings saved');
    } catch (e) { toast.error('Failed'); }
  };

  // ===== Coin Packages =====
  const handlePackagesSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/coin-packages`, { packages: coinPackages }, { withCredentials: true });
      toast.success('Packages saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const updatePackage = (idx, field, val) => {
    const next = [...coinPackages];
    next[idx] = { ...next[idx], [field]: parseInt(val) || 0 };
    setCoinPackages(next);
  };
  const removePackage = (idx) => setCoinPackages(coinPackages.filter((_, i) => i !== idx));
  const addPackage = () => setCoinPackages([...coinPackages, { amount: 10000 }]);

  // ===== Change Password =====
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm) {
      toast.error('New password and confirm do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { withCredentials: true });
      toast.success('Password updated successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // ===== Wallet Adjust =====
  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = adjustForm.currency === 'tickets' ? '/admin/tickets/adjust' : '/admin/wallet/adjust';
      await axios.post(`${API}${endpoint}`, {
        user_id: adjustModal,
        amount: parseInt(adjustForm.amount),
        description: adjustForm.description || 'Admin adjustment'
      }, { withCredentials: true });
      toast.success(`${adjustForm.currency === 'tickets' ? 'Tickets' : 'Coins'} adjusted`);
      setAdjustModal(null);
      setAdjustForm({ amount: '', description: '', currency: 'coins' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const inputStyle = { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };
  const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' };
  const goldBtn = { background: '#D4AF37', color: '#080C0A' };

  return (
    <div className="min-h-screen pb-32" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8, 12, 10, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.2), rgba(3,32,20,0.4))', border: '1px solid rgba(212,175,55,0.3)' }}>
              <Shield className="w-6 h-6" style={{ color: '#D4AF37' }} />
            </div>
            <div>
              <h1 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Admin Dashboard</h1>
              <p className="text-sm" style={{ color: '#A3B8AC' }}>Manage your store</p>
            </div>
          </div>

          <div className="flex gap-3 overflow-x-auto hide-scrollbar">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <motion.button
                  key={tab.id}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-6 py-3 rounded-full font-medium whitespace-nowrap"
                  style={{
                    background: isActive ? '#D4AF37' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${isActive ? '#D4AF37' : 'rgba(255,255,255,0.1)'}`,
                    color: isActive ? '#080C0A' : '#F8FAF9'
                  }}
                  data-testid={`admin-tab-${tab.id}`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </motion.button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
          </div>
        )}

        {/* ===== KUJI / GACHA ===== */}
        {activeTab === 'kuji' && <KujiAdminPanel />}

        {/* ===== HERO BANNERS ===== */}
        {activeTab === 'hero' && <HeroBannersManager />}

        {/* ===== PROMO BANNERS ===== */}
        {activeTab === 'promo' && <PromoBannersManager />}

        {/* ===== SHIPPING ORIGIN ===== */}
        {activeTab === 'shipping-origin' && <ShippingOriginManager />}
        {activeTab === 'categories' && <CategoriesManager />}
        {activeTab === 'tiers' && <TiersManager />}

        {/* ===== PRODUCTS ===== */}
        {!loading && activeTab === 'products' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Product Management</h2>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => { setShowProductForm(!showProductForm); setEditingProduct(null); resetProductForm(); }}
                className="flex items-center gap-2 px-6 py-3 rounded-full font-medium"
                style={goldBtn}
                data-testid="add-product-btn"
              >
                {showProductForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                {showProductForm ? 'Cancel' : 'Add Product'}
              </motion.button>
            </div>

            {showProductForm && (
              <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-6 mb-8 backdrop-blur-xl" style={cardStyle}>
                <h3 className="text-xl font-medium mb-6" style={{ color: '#F8FAF9' }}>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
                <form onSubmit={handleProductSubmit} className="grid md:grid-cols-2 gap-4" data-testid="product-form">
                  <input type="text" placeholder="Product Name" required value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="product-name-input" />
                  <select required value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="product-category-input">
                    <option value="">— Pilih Kategori / Brand —</option>
                    {categories.map((c) => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                  </select>
                  <textarea placeholder="Description" required value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} className="md:col-span-2 w-full rounded-lg px-4 py-3 outline-none" rows="3" style={inputStyle} data-testid="product-description-input" />
                  <input type="number" placeholder="Price (IDR)" required value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="product-price-input" />
                  <input type="number" placeholder="Original Price (optional)" value={productForm.original_price} onChange={(e) => setProductForm({ ...productForm, original_price: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} />
                  <input type="number" placeholder="Stock" required value={productForm.stock} onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="product-stock-input" />
                  <input type="number" min="1" placeholder="Berat produk (gram) *" required value={productForm.weight_grams} onChange={(e) => setProductForm({ ...productForm, weight_grams: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="product-weight-input" />
                  <input type="number" placeholder="Discount %" value={productForm.discount_percent} onChange={(e) => setProductForm({ ...productForm, discount_percent: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} />
                  <div className="md:col-span-2">
                    <ImageGalleryUpload value={productForm.images} onChange={(imgs) => setProductForm({ ...productForm, images: imgs })} required label="Product Images (min 1)" />
                  </div>
                  <div className="md:col-span-2 rounded-xl p-4" style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.15)' }}>
                    <ProductVariantManager
                      value={productForm.variants}
                      onChange={(vars) => setProductForm({ ...productForm, variants: vars })}
                    />
                  </div>
                  <label className="md:col-span-2 flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={productForm.is_flash_sale} onChange={(e) => setProductForm({ ...productForm, is_flash_sale: e.target.checked })} className="w-5 h-5 rounded" />
                    <span style={{ color: '#F8FAF9' }}>Flash Sale</span>
                  </label>
                  {productForm.is_flash_sale && (
                    <>
                      <input type="datetime-local" value={productForm.flash_sale_end_time} onChange={(e) => setProductForm({ ...productForm, flash_sale_end_time: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} />
                      <input type="number" placeholder="Flash Sale Stock" value={productForm.flash_sale_stock} onChange={(e) => setProductForm({ ...productForm, flash_sale_stock: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} />
                    </>
                  )}
                  <motion.button whileTap={{ scale: 0.98 }} type="submit" className="md:col-span-2 w-full py-3 rounded-full font-semibold" style={goldBtn} data-testid="product-submit-btn">
                    {editingProduct ? 'Update Product' : 'Add Product'}
                  </motion.button>
                </form>
              </motion.div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => (
                <div key={p.id} className="rounded-2xl overflow-hidden backdrop-blur-xl" style={cardStyle} data-testid={`admin-product-${p.id}`}>
                  <img
                    src={(p.images && p.images[0]) || p.image_url || ''}
                    alt={p.name}
                    className="w-full h-48 object-cover"
                    style={{ background: 'rgba(0,0,0,0.2)' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 150"><rect fill="%23202824" width="200" height="150"/><text x="100" y="75" fill="%23A3B8AC" font-family="sans-serif" font-size="11" text-anchor="middle" dy=".3em">Image unavailable</text></svg>';
                    }}
                    data-testid="admin-product-thumb"
                  />
                  <div className="p-4">
                    <h3 className="font-semibold mb-1" style={{ color: '#F8FAF9' }}>{p.name}</h3>
                    <p className="text-sm mb-2" style={{ color: '#A3B8AC' }}>{p.category}</p>
                    <p className="font-bold mb-2" style={{ color: '#D4AF37' }}>{formatPrice(p.price)}</p>
                    <p className="text-sm mb-3" style={{ color: '#A3B8AC' }}>Stock: {p.stock}</p>
                    <div className="flex gap-2">
                      <button onClick={() => handleEditProduct(p)} className="flex-1 py-2 rounded-lg flex items-center justify-center gap-1" style={{ background: 'rgba(212,175,55,0.2)', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }} data-testid="edit-product-btn">
                        <Edit className="w-4 h-4" /> Edit
                      </button>
                      <button onClick={() => handleDeleteProduct(p.id)} className="flex-1 py-2 rounded-lg flex items-center justify-center gap-1" style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }} data-testid="delete-product-btn">
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ===== ORDERS ===== */}
        {!loading && activeTab === 'orders' && (
          <>
            <h2 className="text-2xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Order Management</h2>
            {orders.length === 0 ? (
              <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={cardStyle}>
                <ShoppingBag className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
                <p style={{ color: '#A3B8AC' }}>No orders yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => {
                  const tone = ORDER_STATUS_TONE[order.status] || ORDER_STATUS_TONE.pending_payment;
                  const isOpen = expandedOrderId === order.id;
                  return (
                    <div key={order.id} className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle} data-testid={`order-${order.id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-mono font-semibold" style={{ color: '#F8FAF9' }}>#{order.id.slice(0, 8)}</p>
                          <p className="text-sm" style={{ color: '#A3B8AC' }}>{new Date(order.created_at).toLocaleString('id-ID')}</p>
                          <p className="text-xs mt-1" style={{ color: '#CFE0D5' }}>{order.shipping_name} · {order.shipping_phone}</p>
                        </div>
                        <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: tone.bg, color: tone.fg }} data-testid={`order-status-${order.id}`}>
                          {ORDER_STATUS_LABEL[order.status] || order.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm" style={{ color: '#A3B8AC' }}>{order.items.length} items {order.tracking_number ? `· Resi: ${order.tracking_number}` : ''}</p>
                        <p className="font-bold text-xl" style={{ fontFamily: playfairFont, color: '#D4AF37' }}>{formatPrice(order.total_amount)}</p>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => setExpandedOrderId(isOpen ? null : order.id)}
                          className="px-4 py-2 rounded-full text-sm font-semibold"
                          style={{ background: isOpen ? 'rgba(255,255,255,0.05)' : '#D4AF37', color: isOpen ? '#F8FAF9' : '#080C0A' }}
                          data-testid={`admin-manage-order-${order.id}`}
                        >
                          {isOpen ? 'Tutup' : 'Manage'}
                        </button>
                        <button
                          onClick={() => navigate(`/orders/${order.id}`)}
                          className="px-4 py-2 rounded-full text-sm"
                          style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9', border: '1px solid rgba(255,255,255,0.1)' }}
                          data-testid={`admin-view-order-${order.id}`}
                        >
                          Lihat Detail
                        </button>
                      </div>
                      {isOpen && (
                        <AdminOrderManager
                          order={order}
                          onClose={() => setExpandedOrderId(null)}
                          onUpdated={(updated) => {
                            setOrders((prev) => prev.map((o) => o.id === updated.id ? updated : o));
                          }}
                          onDeleted={(deletedId) => {
                            setOrders((prev) => prev.filter((o) => o.id !== deletedId));
                            setExpandedOrderId(null);
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ===== PAYMENTS ===== */}
        {!loading && activeTab === 'payments' && (
          <>
            <h2 className="text-2xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Pending Payment Verifications</h2>
            {payments.length === 0 ? (
              <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={cardStyle}>
                <CreditCard className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
                <p style={{ color: '#A3B8AC' }}>No pending payments</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {payments.map((p) => (
                  <div key={p.id} className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle} data-testid={`payment-${p.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-medium" style={{ color: '#F8FAF9' }}>{p.user_email}</p>
                        <p className="text-sm" style={{ color: '#A3B8AC' }}>{new Date(p.created_at).toLocaleString('id-ID')}</p>
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase" style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }}>{p.payment_method}</span>
                    </div>
                    <p className="text-2xl font-light mb-1" style={{ fontFamily: playfairFont, color: '#D4AF37' }} data-testid={`payment-total-${p.id}`}>
                      {formatPrice(p.total_transfer ?? p.amount)}
                    </p>
                    <div className="mb-3 text-xs space-y-1 rounded-lg p-2.5" style={{ background: 'rgba(0,0,0,0.25)' }}>
                      <div className="flex justify-between"><span style={{ color: '#A3B8AC' }}>Base Amount:</span><span style={{ color: '#F8FAF9' }}>{formatPrice(p.base_amount ?? (p.amount - (p.unique_code || 0)))}</span></div>
                      {p.unique_code != null && p.unique_code > 0 && (
                        <div className="flex justify-between"><span style={{ color: '#A3B8AC' }}>Unique Code:</span><span style={{ color: '#F59E0B' }}>+ Rp {p.unique_code}</span></div>
                      )}
                      <div className="flex justify-between"><span style={{ color: '#A3B8AC' }}>Total Transfer:</span><span style={{ color: '#D4AF37' }}>{formatPrice(p.total_transfer ?? p.amount)}</span></div>
                      <div className="flex justify-between pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}><span style={{ color: '#A3B8AC' }}>Coin Credited:</span><span style={{ color: '#22C55E' }}>{formatCoins(p.coins)} Coin</span></div>
                    </div>
                    {p.payment_proof_url && (
                      <a href={p.payment_proof_url} target="_blank" rel="noreferrer">
                        <img src={p.payment_proof_url} alt="proof" className="w-full h-40 object-cover rounded-lg mb-4" onError={(e) => e.target.style.display = 'none'} />
                      </a>
                    )}
                    <div className="flex gap-2">
                      <button onClick={() => handleVerifyPayment(p.id, 'approve')} className="flex-1 py-2 rounded-lg flex items-center justify-center gap-1 font-semibold" style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', color: '#22C55E' }} data-testid={`approve-payment-${p.id}`}>
                        <Check className="w-4 h-4" /> Approve
                      </button>
                      <button onClick={() => handleVerifyPayment(p.id, 'reject')} className="flex-1 py-2 rounded-lg flex items-center justify-center gap-1 font-semibold" style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)', color: '#EF4444' }} data-testid={`reject-payment-${p.id}`}>
                        <Ban className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ===== CAMPAIGNS ===== */}
        {!loading && activeTab === 'campaigns' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Lucky Draw Campaigns</h2>
              <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowCampaignForm(!showCampaignForm)} className="flex items-center gap-2 px-6 py-3 rounded-full font-medium" style={goldBtn} data-testid="add-campaign-btn">
                {showCampaignForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                {showCampaignForm ? 'Cancel' : 'New Campaign'}
              </motion.button>
            </div>

            {showCampaignForm && (
              <motion.form onSubmit={handleCampaignSubmit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl p-6 mb-8 backdrop-blur-xl grid md:grid-cols-2 gap-4" style={cardStyle} data-testid="campaign-form">
                <input type="text" placeholder="Campaign Name" required value={campaignForm.name} onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })} className="rounded-lg px-4 py-3 outline-none" style={inputStyle} />
                <input type="number" placeholder="Coin Cost per Draw" required value={campaignForm.coin_cost} onChange={(e) => setCampaignForm({ ...campaignForm, coin_cost: e.target.value })} className="rounded-lg px-4 py-3 outline-none" style={inputStyle} />
                <textarea placeholder="Description" required value={campaignForm.description} onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })} className="md:col-span-2 rounded-lg px-4 py-3 outline-none" rows="2" style={inputStyle} />
                <div>
                  <label className="block text-sm mb-1" style={{ color: '#A3B8AC' }}>Start Date</label>
                  <input type="datetime-local" required value={campaignForm.start_date} onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: '#A3B8AC' }}>End Date</label>
                  <input type="datetime-local" required value={campaignForm.end_date} onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} />
                </div>
                <motion.button whileTap={{ scale: 0.98 }} type="submit" className="md:col-span-2 py-3 rounded-full font-semibold" style={goldBtn}>Create Campaign</motion.button>
              </motion.form>
            )}

            {campaigns.length === 0 ? (
              <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={cardStyle}>
                <Sparkles className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
                <p style={{ color: '#A3B8AC' }}>No campaigns yet</p>
              </div>
            ) : (
              <div className="space-y-6">
                {campaigns.map((c) => (
                  <div key={c.id} className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle} data-testid={`campaign-${c.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-xl font-medium" style={{ color: '#F8FAF9' }}>{c.name}</h3>
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: c.active ? 'rgba(34,197,94,0.2)' : 'rgba(163,184,172,0.2)', color: c.active ? '#22C55E' : '#A3B8AC' }}>
                            {c.active ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                        </div>
                        <p className="text-sm mb-1" style={{ color: '#A3B8AC' }}>{c.description}</p>
                        <p className="text-sm" style={{ color: '#D4AF37' }}>{formatCoins(c.coin_cost)} coins/draw</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleToggleCampaign(c)} className="px-4 py-2 rounded-lg text-sm" style={{ background: 'rgba(212,175,55,0.2)', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }} data-testid={`toggle-campaign-${c.id}`}>
                          {c.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => setShowPrizeForm(showPrizeForm === c.id ? null : c.id)} className="px-4 py-2 rounded-lg text-sm flex items-center gap-1" style={goldBtn} data-testid={`add-prize-${c.id}`}>
                          <Plus className="w-4 h-4" /> Prize
                        </button>
                      </div>
                    </div>

                    {showPrizeForm === c.id && (
                      <form onSubmit={handlePrizeSubmit} className="grid md:grid-cols-2 gap-3 p-4 rounded-xl mb-4" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(212,175,55,0.2)' }} data-testid="prize-form">
                        <input type="text" placeholder="Prize Name" required value={prizeForm.name} onChange={(e) => setPrizeForm({ ...prizeForm, name: e.target.value })} className="rounded-lg px-3 py-2 outline-none" style={inputStyle} />
                        <select value={prizeForm.rarity} onChange={(e) => setPrizeForm({ ...prizeForm, rarity: e.target.value })} className="rounded-lg px-3 py-2 outline-none" style={inputStyle}>
                          <option value="common">Common</option>
                          <option value="rare">Rare</option>
                          <option value="epic">Epic</option>
                          <option value="legendary">Legendary</option>
                        </select>
                        <input type="text" placeholder="Description" required value={prizeForm.description} onChange={(e) => setPrizeForm({ ...prizeForm, description: e.target.value })} className="md:col-span-2 rounded-lg px-3 py-2 outline-none" style={inputStyle} />
                        <div className="md:col-span-2">
                          <ImageUpload value={prizeForm.image_url} onChange={(v) => setPrizeForm({ ...prizeForm, image_url: v })} label="Prize Image" required testId="prize-image-upload" />
                        </div>
                        <input type="number" step="0.01" placeholder="Probability (0-1)" required value={prizeForm.probability} onChange={(e) => setPrizeForm({ ...prizeForm, probability: e.target.value })} className="rounded-lg px-3 py-2 outline-none" style={inputStyle} />
                        <input type="number" placeholder="Stock" required value={prizeForm.stock} onChange={(e) => setPrizeForm({ ...prizeForm, stock: e.target.value })} className="rounded-lg px-3 py-2 outline-none" style={inputStyle} />
                        <button type="submit" className="md:col-span-2 py-2 rounded-full font-semibold" style={goldBtn}>Add Prize</button>
                      </form>
                    )}

                    {campaignPrizes[c.id] && campaignPrizes[c.id].length > 0 && (
                      <div className="grid md:grid-cols-4 gap-3">
                        {campaignPrizes[c.id].map((pz) => (
                          <div key={pz.id} className="rounded-xl p-3" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <img src={pz.image_url} alt={pz.name} className="w-full h-24 object-cover rounded-lg mb-2" />
                            <p className="text-sm font-medium" style={{ color: '#F8FAF9' }}>{pz.name}</p>
                            <p className="text-xs uppercase" style={{ color: '#D4AF37' }}>{pz.rarity}</p>
                            <p className="text-xs" style={{ color: '#A3B8AC' }}>Prob: {pz.probability} | Stock: {pz.stock}</p>
                            <button onClick={() => handleDeletePrize(pz.id)} className="mt-2 w-full py-1 text-xs rounded" style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }}>Delete</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ===== SHIPPING ===== */}
        {!loading && activeTab === 'shipping' && (
          <>
            <h2 className="text-2xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Shipping Requests</h2>
            {shippingRequests.length === 0 ? (
              <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={cardStyle}>
                <Truck className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
                <p style={{ color: '#A3B8AC' }}>No shipping requests</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {shippingRequests.map((item) => (
                  <div key={item.id} className="rounded-2xl p-4 backdrop-blur-xl" style={cardStyle} data-testid={`shipping-${item.id}`}>
                    <img src={item.prize_image} alt={item.prize_name} className="w-full h-32 object-cover rounded-lg mb-3" />
                    <p className="font-medium mb-1" style={{ color: '#F8FAF9' }}>{item.prize_name}</p>
                    <p className="text-xs uppercase mb-1" style={{ color: '#D4AF37' }}>{item.rarity}</p>
                    <p className="text-xs mb-3" style={{ color: '#A3B8AC' }}>User: {item.user_id?.slice(0, 8)}</p>
                    <p className="text-xs mb-3" style={{ color: '#A3B8AC' }}>Status: {item.status}</p>
                    {item.status !== 'shipped' && (
                      <button onClick={() => handleMarkShipped(item.id)} className="w-full py-2 rounded-lg font-semibold" style={goldBtn} data-testid={`ship-${item.id}`}>
                        Mark as Shipped
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ===== SETTINGS ===== */}
        {!loading && activeTab === 'settings' && (
          <>
            <h2 className="text-2xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Payment Settings</h2>
            <form onSubmit={handleSettingsSubmit} className="rounded-2xl p-6 backdrop-blur-xl space-y-4 max-w-2xl" style={cardStyle} data-testid="settings-form">
              <div>
                <ImageUpload value={paymentSettings.qris_image_url} onChange={(v) => setPaymentSettings({ ...paymentSettings, qris_image_url: v })} label="QRIS Image" testId="qris-image-upload" />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Bank Name</label>
                <input type="text" value={paymentSettings.bank_name || 'Mandiri'} onChange={(e) => setPaymentSettings({ ...paymentSettings, bank_name: e.target.value })} placeholder="Mandiri / BCA / BNI / BRI" className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="bank-name-input" />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Account Number</label>
                <input type="text" value={paymentSettings.mandiri_account_number || ''} onChange={(e) => setPaymentSettings({ ...paymentSettings, mandiri_account_number: e.target.value })} placeholder="1234567890" className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="mandiri-num-input" />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Account Holder Name</label>
                <input type="text" value={paymentSettings.mandiri_account_holder || ''} onChange={(e) => setPaymentSettings({ ...paymentSettings, mandiri_account_holder: e.target.value })} placeholder="Name" className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="mandiri-holder-input" />
              </div>
              <motion.button whileTap={{ scale: 0.98 }} type="submit" className="w-full py-3 rounded-full font-semibold" style={goldBtn} data-testid="save-settings-btn">Save Settings</motion.button>
            </form>

            <h2 className="text-2xl font-light mt-12 mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Coin Packages</h2>
            <form onSubmit={handlePackagesSubmit} className="rounded-2xl p-6 backdrop-blur-xl space-y-3 max-w-2xl" style={cardStyle} data-testid="packages-form">
              <p className="text-sm mb-3" style={{ color: '#A3B8AC' }}>Customers see these packages on Top Up page. <span style={{ color: '#D4AF37' }}>1 Coin = Rp1.000</span>.</p>
              {coinPackages.map((pkg, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`package-row-${i}`}>
                  <div className="flex-1">
                    <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Amount (Rp) — {Math.floor((pkg.amount || 0) / 1000)} Coin</label>
                    <input type="number" value={pkg.amount} onChange={(e) => updatePackage(i, 'amount', e.target.value)} className="w-full rounded-lg px-3 py-2 outline-none" style={inputStyle} />
                  </div>
                  <button type="button" onClick={() => removePackage(i)} className="self-end p-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }} data-testid={`remove-pkg-${i}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={addPackage} className="flex-1 py-2 rounded-full flex items-center justify-center gap-2" style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9' }} data-testid="add-pkg-btn">
                  <Plus className="w-4 h-4" /> Add Package
                </button>
                <button type="submit" className="flex-1 py-2 rounded-full font-semibold" style={goldBtn} data-testid="save-packages-btn">Save Packages</button>
              </div>
            </form>

            {/* Coin Economy Settings */}
            <h2 className="text-2xl font-light mt-12 mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Coin Economy</h2>
            <div className="rounded-2xl p-6 backdrop-blur-xl max-w-2xl space-y-3" style={cardStyle} data-testid="coin-economy-card">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm" style={{ color: '#A3B8AC' }}>Active rate</p>
                  <p className="text-lg font-medium" style={{ color: '#D4AF37' }}>1 Coin = Rp1.000</p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs" style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}>Locked</span>
              </div>
              <p className="text-xs" style={{ color: '#A3B8AC' }}>
                Rate ini berlaku untuk seluruh sistem (Topup, Kuji, Buyback, Cashback). Migrasi data dari rate lama dijalankan otomatis saat backend startup dan bersifat idempotent (tidak akan berjalan dua kali).
              </p>
            </div>

            <h2 className="text-2xl font-light mt-12 mb-6 flex items-center gap-3" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
              <Lock className="w-5 h-5" style={{ color: '#D4AF37' }} /> Change Password
            </h2>
            <form onSubmit={handlePasswordSubmit} className="rounded-2xl p-6 backdrop-blur-xl space-y-4 max-w-2xl" style={cardStyle} data-testid="change-password-form">
              <div>
                <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Current Password</label>
                <input type="password" required value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="current-password-input" />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>New Password (min 6 chars)</label>
                <input type="password" required minLength={6} value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="new-password-input" />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>Confirm New Password</label>
                <input type="password" required value={passwordForm.confirm} onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="confirm-password-input" />
              </div>
              <motion.button whileTap={{ scale: 0.98 }} type="submit" className="w-full py-3 rounded-full font-semibold" style={goldBtn} data-testid="change-password-btn">Change Password</motion.button>
            </form>
          </>
        )}

        {/* ===== USERS ===== */}
        {!loading && activeTab === 'users' && (
          <UsersTabWithAnalytics
            usersList={usersList}
            setUsersList={setUsersList}
            setBalanceModalUser={setBalanceModalUser}
            cardStyle={cardStyle}
            goldBtn={goldBtn}
            formatCoins={formatCoins}
          />
        )}
        {/* legacy users table (kept hidden so old code still compiles) */}
        {false && !loading && activeTab === 'users' && (
          <>
            <h2 className="text-2xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>User Management</h2>
            <div className="rounded-2xl overflow-hidden backdrop-blur-xl" style={cardStyle}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead style={{ background: 'rgba(0,0,0,0.3)' }}>
                    <tr style={{ color: '#A3B8AC' }}>
                      <th className="text-left px-4 py-3">Name</th>
                      <th className="text-left px-4 py-3">Email</th>
                      <th className="text-left px-4 py-3">Phone</th>
                      <th className="text-left px-4 py-3">Role</th>
                      <th className="text-right px-4 py-3">Coins</th>
                      <th className="text-right px-4 py-3">Tickets</th>
                      <th className="text-right px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map((u) => (
                      <tr key={u.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)', color: '#F8FAF9' }} data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-3">{u.name}</td>
                        <td className="px-4 py-3" style={{ color: '#A3B8AC' }}>{u.email}</td>
                        <td className="px-4 py-3" style={{ color: '#A3B8AC' }}>
                          {u.phone ? (
                            <span className="flex items-center gap-1">
                              {u.phone}
                              {u.phone_verified ? (
                                <span title="Verified" className="text-xs" style={{ color: '#22C55E' }}>✓</span>
                              ) : (
                                <span title="Unverified" className="text-xs" style={{ color: '#EF4444' }}>✗</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-xs italic">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: u.role === 'admin' ? 'rgba(212,175,55,0.2)' : 'rgba(163,184,172,0.2)', color: u.role === 'admin' ? '#D4AF37' : '#A3B8AC' }}>
                            {u.role?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold" style={{ color: '#D4AF37' }}>
                          <div>{formatCoins(u.coin_balance || 0)}</div>
                          <div className="text-[10px]" style={{ color: '#A3B8AC' }}>≈ Rp{formatCoins((u.coin_balance || 0) * 1000)}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold" style={{ color: '#D4AF37' }}>{formatCoins(u.ticket_balance || 0)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-1 justify-end flex-wrap">
                            <button onClick={() => setBalanceModalUser(u)} className="px-3 py-1 rounded-full text-xs font-semibold" style={goldBtn} data-testid={`adjust-${u.id}`}>
                              Manage Balance
                            </button>
                            {!u.email_verified && (
                              <>
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/admin/users/${u.id}/verify-email`, {}, { withCredentials: true });
                                      toast.success('Email diverifikasi');
                                      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
                                      setUsersList(data);
                                    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
                                  }}
                                  className="px-3 py-1 rounded-full text-xs"
                                  style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}
                                  data-testid={`admin-verify-${u.id}`}
                                  title="Manual verify email"
                                >Verify</button>
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API}/admin/users/${u.id}/resend-otp`, {}, { withCredentials: true });
                                      toast.success('OTP dikirim ulang');
                                    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
                                  }}
                                  className="px-3 py-1 rounded-full text-xs"
                                  style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}
                                  data-testid={`admin-resend-${u.id}`}
                                  title="Resend OTP"
                                >Resend OTP</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {balanceModalUser && (
              <AdminBalanceModal
                user={balanceModalUser}
                onClose={() => setBalanceModalUser(null)}
                onUpdated={(updated) => {
                  setUsersList((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
                }}
              />
            )}
          </>
        )}

        {/* ===== TRANSACTIONS ===== */}
        {!loading && activeTab === 'transactions' && (
          <>
            <h2 className="text-2xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Coin Transactions</h2>
            {transactionsList.length === 0 ? (
              <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={cardStyle}>
                <Coins className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
                <p style={{ color: '#A3B8AC' }}>No transactions yet</p>
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden backdrop-blur-xl" style={cardStyle}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead style={{ background: 'rgba(0,0,0,0.3)' }}>
                      <tr style={{ color: '#A3B8AC' }}>
                        <th className="text-left px-4 py-3">When</th>
                        <th className="text-left px-4 py-3">User</th>
                        <th className="text-left px-4 py-3">Type</th>
                        <th className="text-left px-4 py-3">Description</th>
                        <th className="text-right px-4 py-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionsList.map((t) => (
                        <tr key={t.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)', color: '#F8FAF9' }} data-testid={`txn-row-${t.id}`}>
                          <td className="px-4 py-3 whitespace-nowrap" style={{ color: '#A3B8AC' }}>{new Date(t.created_at).toLocaleString('id-ID')}</td>
                          <td className="px-4 py-3 font-mono text-xs" style={{ color: '#A3B8AC' }}>{t.user_id?.slice(0, 10)}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }}>{t.type}</span>
                          </td>
                          <td className="px-4 py-3" style={{ color: '#A3B8AC' }}>{t.description}</td>
                          <td className="px-4 py-3 text-right font-semibold" style={{ color: t.amount > 0 ? '#22C55E' : '#EF4444' }}>
                            {t.amount > 0 ? '+' : ''}{formatCoins(t.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== DRAW LOGS ===== */}
        {!loading && activeTab === 'drawlogs' && (
          <>
            <h2 className="text-2xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Lucky Draw Logs</h2>
            {drawLogs.length === 0 ? (
              <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={cardStyle}>
                <History className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
                <p style={{ color: '#A3B8AC' }}>No draws yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {drawLogs.map((log) => (
                  <div key={log.id} className="rounded-2xl p-4 backdrop-blur-xl flex gap-3" style={cardStyle} data-testid={`drawlog-${log.id}`}>
                    <img src={log.prize_image} alt={log.prize_name} className="w-20 h-20 rounded-xl object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                    <div className="flex-1">
                      <p className="font-medium" style={{ color: '#F8FAF9' }}>{log.prize_name}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold uppercase" style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }}>{log.rarity}</span>
                      <p className="text-xs mt-2 font-mono" style={{ color: '#A3B8AC' }}>User: {log.user_id?.slice(0, 10)}</p>
                      <p className="text-xs" style={{ color: '#A3B8AC' }}>{new Date(log.created_at).toLocaleString('id-ID')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ===== OTP LOGS ===== */}
        {!loading && activeTab === 'otplogs' && (
          <>
            <h2 className="text-2xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>OTP & Email Logs</h2>
            <p className="text-sm mb-6" style={{ color: '#A3B8AC' }}>Audit semua pengiriman OTP (registrasi, resend, reset password). Status `fallback` berarti email service belum dikonfigurasi (RESEND_API_KEY).</p>
            {otpLogs.length === 0 ? (
              <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={cardStyle}>
                <Shield className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
                <p style={{ color: '#A3B8AC' }}>No OTP logs yet</p>
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden backdrop-blur-xl" style={cardStyle}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="otp-logs-table">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#A3B8AC' }}>
                        <th className="px-3 py-2 text-left text-xs font-medium">Time</th>
                        <th className="px-3 py-2 text-left text-xs font-medium">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-medium">Purpose</th>
                        <th className="px-3 py-2 text-left text-xs font-medium">Delivery</th>
                        <th className="px-3 py-2 text-left text-xs font-medium">Verify</th>
                        <th className="px-3 py-2 text-left text-xs font-medium">Error / Message ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otpLogs.map((log) => (
                        <tr key={log.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#F8FAF9' }} data-testid={`otp-log-${log.id}`}>
                          <td className="px-3 py-2 text-xs" style={{ color: '#A3B8AC' }}>{new Date(log.otp_sent_at).toLocaleString('id-ID')}</td>
                          <td className="px-3 py-2 text-xs">{log.email}</td>
                          <td className="px-3 py-2 text-xs">{log.otp_purpose}</td>
                          <td className="px-3 py-2 text-xs">
                            <span className="px-2 py-0.5 rounded-full" style={{
                              background: log.delivery_status === 'sent' ? 'rgba(34,197,94,0.15)'
                                : log.delivery_status === 'fallback' ? 'rgba(245,158,11,0.15)'
                                : 'rgba(239,68,68,0.15)',
                              color: log.delivery_status === 'sent' ? '#22C55E'
                                : log.delivery_status === 'fallback' ? '#F59E0B'
                                : '#EF4444',
                            }}>{log.delivery_status}</span>
                          </td>
                          <td className="px-3 py-2 text-xs" style={{ color: log.verification_status === 'verified' ? '#22C55E' : '#A3B8AC' }}>
                            {log.verification_status}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono" style={{ color: '#A3B8AC', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.delivery_error || log.message_id || ''}>
                            {log.delivery_error || log.message_id || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <MobileNav />
    </div>
  );
};
